<template>
  <div class="ae">

    <div ref="grid" class="ae-grid" :class="{ 'is-resizing': resizing }" :style="convWidth ? { '--ae-conv': `${convWidth}px` } : null">
      <AgentConversation
        ref="conversation"
        :entry="entry"
        :context="context"
        :session-id="sessionId"
        :storage-key="storageKey"
        :auto-start="autoStart"
        @session="onSession"
        @reset="onReset"
        @goto-artifact="scrollToArtifact"
      />

      <div
        class="ae-splitter"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize conversation"
        title="Drag to resize; double-click to reset"
        @pointerdown="startResize"
        @dblclick="resetSplit"
      />

      <!-- ─────────── Workspace: notebook + artifacts ─────────── -->
      <section class="ae-card ae-workspace" aria-label="Workspace">
        <div class="ae-work-head">
          <div class="ae-card-head">
            <div class="ae-title">Workspace</div>
            <div class="ae-head-right">
              <a v-if="canUpload" href="#" class="ae-link ae-btn-tiny ae-btn-icon" title="Upload a file for the agent to read" aria-label="Upload" @click.prevent="showUpload = true"><font-awesome-icon :icon="uploadIcon" /></a>
              <span class="ae-state">
                <span class="ae-dot" :style="{ background: sessionPill.dot }" />
                <span>{{ sessionPill.text }}</span>
              </span>
            </div>
          </div>
          <div v-if="lanes.length > 1" class="ae-lanes" role="tablist" aria-label="Filter workspace">
            <button
              v-for="l in lanes"
              :key="l.id"
              type="button"
              role="tab"
              class="ae-lane"
              :class="{ 'is-on': lane === l.id }"
              :aria-selected="lane === l.id ? 'true' : 'false'"
              @click="lane = l.id"
            >
              {{ l.label }}<span class="ae-lane-n">{{ l.count }}</span>
            </button>
          </div>
        </div>

        <div class="ae-work-body">
        <div ref="artifactScroll" class="ae-stack">
          <div v-if="!notebookItems.length" class="ae-empty">
            <div class="ae-empty-body">Every query the agent runs lands here, and so does any page it writes. The newest opens automatically; click a header to open an earlier one.</div>
          </div>

          <div
            v-for="a in visibleNotebook"
            :key="a.key"
            :id="artifactDomId(a.key)"
            class="ae-fold"
            :class="{ 'is-open': isOpen(a), 'is-unsaved': isUnsaved(a) }"
          >
            <div class="ae-fold-row">
            <button type="button" class="ae-fold-head" @click="toggleFold(a)">
              <span class="ae-fold-caret">{{ isOpen(a) ? '▾' : '▸' }}</span>
              <font-awesome-icon :icon="kindIcon(rowIcon(a))" class="ae-kind-icon" :style="{ color: kindColor(rowIcon(a)) }" />
              <span class="ae-fold-kind">{{ rowLabel(a) }}</span>
              <span class="ae-fold-name sensitive">{{ rowName(a) }}</span>
              <span v-if="a.kind === 'draft'" class="ae-draft-chip" :class="`is-${a.status}`">{{ draftChip(a) }}</span>
              <span v-if="isUnsaved(a)" class="ae-unsaved-pill">Unsaved</span>
              <span class="ae-dot" :style="{ background: rowDot(a) }" />
            </button>
            <button v-if="a.kind !== 'draft'" type="button" class="ae-x" title="Remove from the workspace" @click.stop="removeArtifact(entry, a)">×</button>
            <span v-if="isOpen(a) && sessionActive" class="ae-fold-actions">
              <template v-if="a.kind === 'cell'">
                <template v-if="a.editing">
                  <a href="#" class="ae-link" @click.prevent="saveEdit(a)">Save</a>
                  <a href="#" class="ae-link" @click.prevent="cancelEdit(a)">Cancel</a>
                </template>
                <template v-else>
                  <a href="#" class="ae-link" @click.prevent="startEdit(a)">Edit</a>
                  <a href="#" class="ae-link" @click.prevent="runCell(a)">Run</a>
                </template>
              </template>
              <template v-else-if="a.kind === 'draft'">
                <template v-if="a.editing">
                  <a href="#" class="ae-link" @click.prevent="saveDraftEdit(a)">Save edit</a>
                  <a href="#" class="ae-link" @click.prevent="cancelDraftEdit(a)">Cancel</a>
                </template>
                <template v-else>
                  <a href="#" class="ae-link" @click.prevent="startDraftEdit(a)">Edit</a>
                  <a v-if="a.kind !== 'schema'" href="#" class="ae-link" @click.prevent="draftAction('preview', a)">Preview</a>
                  <a v-if="a.status !== 'saved' && canSaveDraft(a)" href="#" class="ae-link" @click.prevent="draftAction('save', a)">Save</a>
                  <a href="#" class="ae-link" @click.prevent="draftAction('discard', a)">Discard</a>
                </template>
              </template>
            </span>
            </div>
            <div v-if="isOpen(a)" class="ae-fold-body">
            <!-- draft -->
            <div v-if="a.kind === 'draft'" :id="artifactDomId(a.key)" class="ae-cell ae-draft" :class="{ 'is-editing': a.editing, 'is-flash': a.flash, 'is-unsaved': isUnsaved(a) }">

              <div v-if="codeField(a)" class="ae-cell-box ae-cell-box-code">
                <div class="ae-cell-strip">
                  <span>{{ codeField(a) === 'script' ? 'R script' : 'SQL' }}</span>
                  <span v-if="a.editing" class="is-muted">editing</span>
                  <span v-else-if="a.by === 'user'" class="is-stale">edited by you · v{{ a.version }}</span>
                  <span v-else class="is-muted">written by the agent · v{{ a.version }}</span>
                </div>
                <div v-if="a.error" class="ae-cell-error sensitive" data-demo-blur>{{ a.error }}</div>
                <div v-if="a.savedAt" class="ae-draft-saved">Saved as #{{ a.savedId }} · {{ formatWhen(a.savedAt) }}</div>
                <textarea
                  v-if="a.editing"
                  v-model="a.edit.code"
                  class="ae-cell-editor sensitive"
                  spellcheck="false"
                  :rows="editorRows(a.edit.code)"
                  @keydown.meta.enter.prevent="saveDraftEdit(a)"
                  @keydown.ctrl.enter.prevent="saveDraftEdit(a)"
                  @keydown.esc.prevent="cancelDraftEdit(a)"
                />
                <pre v-else class="ae-cell-pre sensitive">{{ draftCode(a) }}</pre>
              </div>
              <div class="ae-cell-box ae-cell-box-code" :class="{ 'ae-cell-box-json': codeField(a) }">
                <div class="ae-cell-strip ae-cell-strip-toggle" @click="toggleDraft(a)">
                  <span><span class="ae-draft-caret">{{ a.collapsed && !a.editing ? '▸' : '▾' }}</span>{{ codeField(a) ? 'Everything else · JSON' : `${a.label} · JSON spec` }}</span>
                  <template v-if="!codeField(a)">
                    <span v-if="a.editing" class="is-muted">editing</span>
                    <span v-else-if="a.by === 'user'" class="is-stale">edited by you · v{{ a.version }}</span>
                    <span v-else class="is-muted">written by the agent · v{{ a.version }}</span>
                  </template>
                </div>
                <template v-if="!codeField(a)">
                  <div v-if="a.error" class="ae-cell-error sensitive" data-demo-blur>{{ a.error }}</div>
                  <div v-if="a.savedAt" class="ae-draft-saved">Saved as #{{ a.savedId }} · {{ formatWhen(a.savedAt) }}</div>
                </template>
                <template v-if="a.editing">
                  <textarea
                    v-model="a.edit.json"
                    class="ae-cell-editor ae-draft-editor ae-mono sensitive"
                    spellcheck="false"
                    :rows="editorRows(a.edit.json)"
                    @keydown.meta.enter.prevent="saveDraftEdit(a)"
                    @keydown.ctrl.enter.prevent="saveDraftEdit(a)"
                    @keydown.esc.prevent="cancelDraftEdit(a)"
                  />
                  <div v-if="a.editError" class="ae-cell-error sensitive" data-demo-blur>{{ a.editError }}</div>
                </template>
                <pre v-else-if="!a.collapsed" class="ae-cell-pre ae-cell-pre-json sensitive">{{ draftJson(a) }}</pre>
              </div>
              <div v-if="a.note && !draftPreview(a)" class="ae-cell-box ae-cell-box-result">
                <div class="ae-cell-strip ae-cell-strip-result">
                  <span>Check · <span class="is-ok">valid</span></span>
                  <span v-if="a.checkedAt" class="is-muted">last check · {{ formatWhen(a.checkedAt) }}</span>
                </div>
                <div class="ae-cell-note ae-draft-check sensitive" data-demo-blur>{{ a.note }}</div>
              </div>
              <template v-if="draftPreview(a)">
                <div class="ae-cell-box ae-cell-box-result">
                  <div class="ae-cell-strip ae-cell-strip-result">
                    <span>Preview<template v-if="draftPreview(a).result"> · <span :class="cellStatus(draftPreview(a)).tone">{{ cellStatus(draftPreview(a)).text }}</span></template></span>
                    <span v-if="draftPreview(a).result && draftPreview(a).result.ranAt" :class="previewStale(a) ? 'is-stale' : 'is-muted'">{{ previewStale(a) ? 'edited since preview' : 'last preview' }} · {{ formatWhen(draftPreview(a).result.ranAt) }}</span>
                  </div>
                  <details v-if="previewDiffers(a)" class="ae-draft-compiled">
                    <summary>Compiled SQL</summary>
                    <pre class="ae-cell-pre sensitive">{{ draftPreview(a).code }}</pre>
                  </details>
                  <div v-if="draftPreview(a).result && draftPreview(a).result.kind === 'sql'" class="ae-cell-result">
                    <div v-if="!draftPreview(a).result.ok" class="ae-cell-error sensitive" data-demo-blur>{{ draftPreview(a).result.error }}</div>
                    <div v-else-if="!draftPreview(a).result.rows.length" class="ae-cell-note">No rows.</div>
                    <div v-else class="ae-cell-scroll sensitive">
                      <table class="ae-table ae-cell-table">
                        <thead><tr><th v-for="c in draftPreview(a).result.columns" :key="c">{{ c }}</th></tr></thead>
                        <tbody>
                          <tr v-for="(r, ri) in draftPreview(a).result.rows" :key="ri"><td v-for="c in draftPreview(a).result.columns" :key="c" class="ae-mono">{{ r[c] }}</td></tr>
                        </tbody>
                      </table>
                    </div>
                    <div v-if="draftPreview(a).result.ok && draftPreview(a).result.truncated" class="ae-cell-note">{{ draftPreview(a).result.rows.length }} of {{ formatCount(draftPreview(a).result.rowCount) }} rows</div>
                  </div>
                </div>
              </template>
            </div>

            <!-- code block -->
            <div v-else-if="a.kind === 'cell'" :id="artifactDomId(a.key)" class="ae-cell" :class="{ 'is-editing': a.editing, 'is-flash': a.flash }">

              <div class="ae-cell-box ae-cell-box-code">
                <div class="ae-cell-strip">
                  <span>{{ a.engine === 'r' ? 'R script' : 'SQL' }}</span>
                  <span v-if="a.editing" class="is-muted">editing</span>
                  <span v-else-if="a.by === 'user'" class="is-stale">edited by you</span>
                  <span v-else class="is-muted">written by the agent</span>
                </div>
                <div class="ae-cell-code">
                  <textarea
                    v-if="a.editing"
                    v-model="a.draft"
                    class="ae-cell-editor sensitive"
                    spellcheck="false"
                    :rows="editorRows(a.draft)"
                    @keydown.meta.enter.prevent="saveEdit(a)"
                    @keydown.ctrl.enter.prevent="saveEdit(a)"
                    @keydown.esc.prevent="cancelEdit(a)"
                  />
                  <pre v-else class="ae-cell-pre sensitive">{{ a.code }}</pre>
                </div>
              </div>
              <div class="ae-cell-box ae-cell-box-result">
                <div class="ae-cell-strip ae-cell-strip-result">
                  <span>Result<template v-if="a.result"> · <span :class="cellStatus(a).tone">{{ cellStatus(a).text }}</span></template></span>
                  <span v-if="a.result && a.result.ranAt" :class="cellStale(a) ? 'is-stale' : 'is-muted'">{{ cellStale(a) ? 'edited since last run' : 'last run' }} · {{ formatWhen(a.result.ranAt) }}</span>
                  <span v-else-if="!a.result" class="is-muted">not run yet</span>
                </div>
                <div v-if="a.result && a.result.kind === 'sql'" class="ae-cell-result">
                  <div v-if="!a.result.ok" class="ae-cell-error sensitive" data-demo-blur>{{ a.result.error }}</div>
                  <div v-else-if="!a.result.rows.length" class="ae-cell-note">No rows.</div>
                  <div v-else class="ae-cell-scroll sensitive">
                    <table class="ae-table ae-cell-table">
                      <thead><tr><th v-for="c in a.result.columns" :key="c">{{ c }}</th></tr></thead>
                      <tbody>
                        <tr v-for="(r, ri) in a.result.rows" :key="ri"><td v-for="c in a.result.columns" :key="c" class="ae-mono">{{ r[c] }}</td></tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-if="a.result.ok && a.result.truncated" class="ae-cell-note">{{ a.result.rows.length }} of {{ formatCount(a.result.rowCount) }} rows</div>
                </div>
                <div v-else-if="a.result && a.result.kind === 'r'" class="ae-cell-result ae-cell-result-r">
                  <AgentRunOutput :status="a.result.status" :error="a.result.error" :console-text="a.result.console" :outputs="a.result.outputs" :plot-urls="a.result.plotUrls" />
                </div>
              </div>
              <div v-if="a.error" class="ae-cell-error sensitive" data-demo-blur>{{ a.error }}</div>
            </div>

            <div v-else :id="artifactDomId(a.key)" class="ae-artifact" :class="{ 'is-flash': a.flash, 'is-unsaved': isUnsaved(a) }">
              <div class="ae-artifact-head">
                <div class="ae-artifact-label">
                  <font-awesome-icon :icon="kindIcon(a.kind)" class="ae-kind-icon" :style="{ color: kindColor(a.kind) }" />
                  <span class="ae-dot" :style="{ background: statusDot(a.status) }" />
                  {{ a.label }}<span v-if="a.name" class="ae-artifact-name sensitive"> · {{ a.name }}</span>
                </div>
                <div v-if="a.versions" class="ae-version-bar">
                  <span v-if="a.versions.length > 1" class="ae-version-nav">
                    <button class="ae-version-btn" :disabled="a.current <= 0" title="Earlier version" @click="showVersion(entry, a.key, a.current - 1)">‹</button>
                    <span class="ae-version-n">v{{ a.versions[a.current].n }} of {{ a.versions.length }}</span>
                    <button class="ae-version-btn" :disabled="a.current >= a.versions.length - 1" title="Later version" @click="showVersion(entry, a.key, a.current + 1)">›</button>
                  </span>
                  <span v-if="a.versions[a.current].saved" class="ae-save-chip is-saved">Saved · {{ a.versions[a.current].saved.slug }}</span>
                  <span v-else class="ae-save-chip is-unsaved">Not saved</span>
                  <a v-if="!a.versions[a.current].saved && canSavePage" href="#" class="ae-link" @click.prevent="savePage(a)">Save</a>
                  <router-link v-else-if="a.versions[a.current].saved && a.versions[a.current].saved.pageId" class="ae-link" :to="`/agent/page/${a.versions[a.current].saved.pageId}`" target="_blank">Open</router-link>
                  <a v-if="a.versions.some((v) => v.saved)" href="#" class="ae-link ae-link-danger" title="Delete the saved page from the platform" @click.prevent="deleteSavedPage(entry, a)">Delete page</a>
                </div>
                <div v-else class="ae-artifact-meta">{{ a.status }}</div>
              </div>

              <!-- visual -->
              <div v-if="a.kind === 'visual'" class="ae-box sensitive">
                <div v-if="a.pending" class="ae-muted">Loading chart{{ a.rowCount ? ` (${a.rowCount.toLocaleString()} rows)` : '' }}…</div>
                <div v-else-if="!a.viz" style="color:#c23a28">Could not render this visual.</div>
                <D3Insight
                  v-else-if="a.viz.component === 'd3'"
                  :code="a.viz.props.code"
                  :rows="a.viz.props.rows"
                  :inputs="a.viz.props.inputs"
                  :min-height="a.viz.props.minHeight"
                />
                <Chart_V6 v-else-if="a.viz.component === 'chart_v6'" :spec="a.viz.props.spec" />
                <div v-else-if="a.viz.component === 'chart_v5'" style="height:300px">
                  <Chart_V5 :chart_data="a.viz.props.chart_data" :chart_config="a.viz.props.chart_config" override-height="h-full" />
                </div>
                <div v-else-if="a.viz.component === 'table'" class="ae-table-scroll">
                  <table class="ae-table">
                    <thead><tr><th v-for="c in a.viz.props.columnDefs" :key="c.field">{{ c.headerName || c.field }}</th></tr></thead>
                    <tbody>
                      <tr v-for="(r, ri) in a.viz.props.rowData" :key="ri">
                        <td v-for="c in a.viz.props.columnDefs" :key="c.field">{{ r[c.field] }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <Pie_Chart v-else-if="a.viz.component === 'pie'" style="height:300px" override-height="h-full" :chart_data="a.viz.props.chart_data" :chart_config="a.viz.props.chart_config" />
                <Sankey_Diagram v-else-if="a.viz.component === 'sankey'" :chart-data="a.viz.props.chartData" :config="a.viz.props.config" />
                <HexMap v-else-if="a.viz.component === 'heatmap'" :chart-data="a.viz.props.chartData" :chart-config="a.viz.props.chartConfig" />
              </div>

              <!-- drawing -->
              <!-- page: a full document the agent wrote, sandboxed -->
              <AgentHtmlCard
                v-else-if="a.kind === 'page'"
                :html="a.html"
                :sources="a.sources || []"
                :session-id="entry.sessionId || ''"
                class="sensitive"
                data-demo-blur
              />

              <PageCanvas
                v-else-if="a.kind === 'dashboard'"
                :key="a.key"
                :spec="a.spec"
                :widgets="a.widgets || {}"
                :session-id="entry.sessionId || ''"
              />

              <div v-else-if="a.kind === 'drawing'" class="ae-box sensitive">
                <iframe :srcdoc="drawingDoc(a)" sandbox="" class="ae-iframe" :style="{ aspectRatio: drawingAspect(a) }" :title="a.name" />
              </div>

              <!-- plan -->
              <div v-else-if="a.kind === 'plan'" class="ae-table-wrap">
                <table class="ae-table">
                  <thead><tr><th>Source</th><th>Role</th><th>Status</th><th>Description</th></tr></thead>
                  <tbody>
                    <tr v-for="src in a.sources" :key="src.name">
                      <td class="ae-mono">{{ src.name }}</td>
                      <td class="ae-muted">{{ src.role || '' }}</td>
                      <td><span class="ae-dot" :style="{ background: statusDot(src.status || 'planned') }" /> {{ src.status || 'planned' }}</td>
                      <td class="ae-muted">{{ src.description || '' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- proposal -->
              <template v-else-if="a.kind === 'proposal'">
                <div class="ae-facts">
                  <div class="ae-fact"><div class="ae-fact-k">Target</div><div class="ae-fact-v ae-mono">{{ a.name }}</div></div>
                  <div class="ae-fact"><div class="ae-fact-k">Key</div><div class="ae-fact-v">{{ a.append_only ? 'Append-only' : ((a.primary_key_columns || []).join(', ') || 'No key') }}</div></div>
                  <div class="ae-fact"><div class="ae-fact-k">Columns</div><div class="ae-fact-v">{{ (a.columns || []).length }}</div></div>
                </div>
                <div v-if="a.append_only && a.append_only_reason" class="ae-small ae-muted">{{ a.append_only_reason }}</div>
                <div v-if="a.error" class="ae-small" style="color:#c23a28">{{ a.error }}</div>
                <div class="ae-table-wrap ae-table-scroll">
                  <table class="ae-table">
                    <thead><tr><th>Column</th><th>Type</th></tr></thead>
                    <tbody>
                      <tr v-for="col in a.columns" :key="col.name || col">
                        <td class="ae-mono">{{ col.name || col }}</td>
                        <td class="ae-muted">{{ col.snowflake_type || 'VARCHAR' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>

              <!-- file -->
              <template v-else-if="a.kind === 'file'">
                <div class="ae-facts">
                  <div class="ae-fact" style="grid-column: span 2"><div class="ae-fact-k">File</div><div class="ae-fact-v ae-mono ae-break">{{ a.key }}</div></div>
                  <div class="ae-fact"><div class="ae-fact-k">Shape</div><div class="ae-fact-v">{{ a.note }}</div></div>
                </div>
                <div v-if="a.rows && a.rows.length" class="ae-table-wrap ae-cell-scroll sensitive">
                  <table class="ae-table ae-cell-table">
                    <thead><tr><th class="ae-file-rownum">row</th><th v-for="(c, ci) in a.rows[0]" :key="ci">{{ ci + 1 }}</th></tr></thead>
                    <tbody>
                      <tr v-for="(r, ri) in a.rows" :key="ri">
                        <td class="ae-mono ae-file-rownum">{{ (a.offset || 0) + ri }}</td>
                        <td v-for="(c, ci) in r" :key="ci" class="ae-mono">{{ c }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>

              <!-- document page -->
              <template v-else-if="a.kind === 'document'">
                <div class="ae-facts">
                  <div class="ae-fact" style="grid-column: span 2"><div class="ae-fact-k">File</div><div class="ae-fact-v ae-mono ae-break">{{ a.key }}</div></div>
                  <div class="ae-fact"><div class="ae-fact-k">Shape</div><div class="ae-fact-v">{{ a.note }}<template v-if="a.total"> · characters {{ a.offset.toLocaleString() }}–{{ a.end.toLocaleString() }} of {{ a.total.toLocaleString() }}</template></div></div>
                </div>
                <pre class="ae-doc-text ae-cell-scroll sensitive" data-demo-blur>{{ a.text }}</pre>
              </template>

              <!-- run without a block (sessions before the notebook) -->
              <AgentRunOutput v-else-if="a.kind === 'run'" :status="a.status" :error="a.error" :console-text="a.console" :outputs="a.outputs" :plot-urls="a.plotUrls" />
              <div v-else-if="a.kind === 'template_run'" class="ae-box">
                <div class="ae-file-fact"><span>Status</span><span>{{ a.status }}</span></div>
                <div v-if="a.progress" class="ae-file-fact"><span>Progress</span><span>{{ a.progress }}</span></div>
                <div class="ae-file-fact"><span>Run</span><span class="ae-mono">{{ a.id }}</span></div>
                <div v-if="a.error" role="alert" class="ae-cell-note">{{ a.error }}</div>
                <button v-if="['pending', 'running'].includes(a.status) && !a.cancelling" type="button" class="ae-btn-tiny" @click="cancelTemplateRun(a)">Cancel run</button>
                <span v-else-if="a.cancelling && ['pending', 'running'].includes(a.status)" class="ae-cell-note">Stopping after the current batch…</span>
              </div>
            </div>
            </div>
          </div>

          <!-- saved artifacts, reports and uploads: same accordion, own rows -->
          <template v-for="g in visibleLedger" :key="g.id">
            <div class="ae-stack-label">{{ g.label }}</div>
            <div
              v-for="it in g.items"
              :id="it.anchor ? artifactDomId(it.key) : undefined"
              :key="it.key"
              class="ae-fold"
              :class="{ 'is-open': it.ref && it.ref.expanded }"
            >
              <button type="button" class="ae-fold-head" :title="it.hint" @click="ledgerClick(it)">
                <span class="ae-fold-caret">{{ it.ref && it.ref.expanded ? '▾' : '▸' }}</span>
                <font-awesome-icon :icon="kindIcon(it.iconKind)" class="ae-kind-icon" :style="{ color: kindColor(it.iconKind) }" />
                <span class="ae-fold-kind">{{ it.label }}</span>
                <span class="ae-fold-name sensitive" :class="{ 'ae-mono': it.mono }">{{ it.name }}</span>
                <span v-if="it.chip" class="ae-draft-chip">{{ it.chip }}</span>
                <span class="ae-dot" :style="{ background: it.dot }" />
              </button>
              <button v-if="it.ref && it.group !== 'uploads'" type="button" class="ae-x" :title="it.ref.kind === 'saved_page' ? 'Delete this page from the platform' : 'Remove from the workspace'" @click.stop="it.ref.kind === 'saved_page' ? deleteSavedPage(entry, it.ref) : removeArtifact(entry, it.ref)">×</button>
              <div v-if="it.ref && it.ref.expanded" class="ae-fold-body">
                <template v-if="it.group === 'uploads'">
                  <div class="ae-file-fact"><span>S3 key</span><span class="ae-mono ae-break">{{ it.ref.key }}</span></div>
                </template>
                <template v-else>
                  <div class="ae-file-fact"><span>Status</span><span>{{ it.ref.status }}</span></div>
                  <div v-for="f in factsFor(it.ref).slice(0, 5)" :key="f.k" class="ae-file-fact"><span>{{ f.k }}</span><span class="sensitive">{{ f.v }}</span></div>
                  <pre v-if="it.ref.script" class="ae-cell-pre ae-file-script sensitive">{{ it.ref.script }}</pre>
                  <div v-for="(r, j) in (it.ref.runs || []).slice(0, 5)" :key="j" class="ae-file-run">
                    <span class="ae-check" :style="['failed','error'].includes(String(r.status).toLowerCase()) ? 'color:#c23a28' : ''">{{ ['failed','error'].includes(String(r.status).toLowerCase()) ? '✕' : '✓' }}</span>
                    <span class="ae-mono ae-file-run-when">{{ formatWhen(r.when) }}</span>
                    <span class="sensitive ae-file-run-text">{{ r.error || r.result }}</span>
                  </div>
                </template>
              </div>
            </div>
          </template>
        </div>
        </div>
      </section>
    </div>

    <AgentUploadModal v-if="showUpload" :session-id="entry.sessionId || ''" @close="showUpload = false" @uploaded="onUploaded" @failed="onUploadFailed" />
  </div>
</template>

<script>
import axios from 'axios';
import { exportBlockedInDemoMode } from '@/utils/demoSpoof';
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue';
import Chart_V6 from '@/components/widgetcomponents/Viz_Elements/Chart_V6.vue';
import Sankey_Diagram from '@/components/widgetcomponents/Viz_Elements/Sankey_Diagram.vue';
import HexMap from '@/components/widgetcomponents/Viz_Elements/HexMap.vue';
import Pie_Chart from '@/components/widgetcomponents/Viz_Elements/Pie_Chart.vue';
import { drawingDoc } from '@/utils/agentSandbox';
import { z } from 'zod';
import { BrowserStore } from '@/utils/BrowserStore';
import AgentConversation from './AgentConversation.vue';
import './agent.css';
import AgentHtmlCard from './AgentHtmlCard.vue';
import D3Insight from './D3Insight.vue';
import PageCanvas from './widgets/PageCanvas.vue';
import AgentUploadModal from './AgentUploadModal.vue';
import AgentRunOutput, { plotKey } from './AgentRunOutput.vue';
import {
  faSnowflake, faDatabase, faFlaskVial, faTableColumns, faGaugeHigh, faLayerGroup, faTableList,
  faChartSimple, faPenRuler, faListCheck, faFileLines, faPlay, faFileArrowUp, faFlag, faClipboardList,
  faLightbulb, faGears, faTable, faFilePdf, faMagnifyingGlassChart, faFileArrowUp as faUploadBtn, faWindowMaximize,
} from '@fortawesome/free-solid-svg-icons';

// v2: the default split is even (50:50). A width saved under the old
// three-column layout would restore a lopsided split, so the key is versioned.
const layoutStore = new BrowserStore('agent-layout-v2', z.object({
  conv: z.number().nullable().default(null),
  rail: z.boolean().default(false),
}));
const CONV_MIN = 340;
const WORKSPACE_MIN = 480;
const SPLITTER_W = 20;

const KIND_ICONS = {
  snowflake: faSnowflake, postgres: faDatabase, r: faFlaskVial,
  data_attribute: faTableColumns, metric: faGaugeHigh, dataset: faLayerGroup, study: faFlaskVial, schema: faTableList,
  visual: faChartSimple, drawing: faPenRuler, plan: faListCheck, file: faFileLines, document: faFileLines, proposal: faTableList, run: faPlay,
  inputs: faTable, template_run: faPlay, upload: faFileArrowUp, report: faFilePdf, flag: faFlag, worklist: faClipboardList, insight: faLightbulb, routine: faGears,
  task_type: faClipboardList, task_action: faPlay, metric_workflow: faListCheck, task_status: faFlag,
  analysis: faMagnifyingGlassChart, page: faWindowMaximize,
};
const KIND_COLORS = {
  snowflake: '#4f9cc4', postgres: '#35617f', r: '#3b6e93',
  data_attribute: '#35617f', metric: '#35617f', dataset: '#2f8f5f', study: '#7a5aa3', schema: '#9a7b1f',
  visual: '#35617f', drawing: '#7a5aa3', plan: '#56534d', file: '#9a7b1f', document: '#7d6434', proposal: '#9a7b1f', run: '#3b6e93',
  inputs: '#2f8f5f', template_run: '#3b6e93', upload: '#35617f', report: '#a33122', flag: '#c23a28', worklist: '#2f8f5f', insight: '#d9a441', routine: '#56534d',
  task_type: '#3b6e93', task_action: '#7a5aa3', metric_workflow: '#35617f', task_status: '#56534d',
  analysis: '#2f8f5f',
};

import { FEATURES } from '@/constants/features';
import { canAccessFeature } from '@/utils/access';
import { entryForSession, turnUrl, applyRunOutput, upsertArtifact, artifactKey, runHolder, applyDraftEdit, showVersion, removeArtifact, deleteSavedPage, DRAFT_LEDGER_KIND, NOTEBOOK_ARTIFACT_KINDS } from '@/services/agentSessionController';

const NOTEBOOK_LANES = {
  queries: { label: 'Queries', match: (a) => a.kind === 'cell' || !a.kind },
  visuals: { label: 'Visuals', match: (a) => a.kind === 'visual' || a.kind === 'drawing' },
  pages: { label: 'Pages', match: (a) => a.kind === 'page' || a.kind === 'dashboard' },
  files: { label: 'Files', match: (a) => a.kind === 'file' || a.kind === 'document' },
  runs: { label: 'Runs', match: (a) => ['run', 'template_run', 'plan', 'proposal', 'inputs'].includes(a.kind) },
};

const DRAFT_SAVE_FEATURE = {
  data_attribute: FEATURES.ACTION_AGENT_SAVE_METRICS, metric: FEATURES.ACTION_AGENT_SAVE_METRICS,
  dataset: FEATURES.ACTION_AGENT_SAVE_STUDIES, study: FEATURES.ACTION_AGENT_SAVE_STUDIES,
  schema: FEATURES.ACTION_AGENT_SAVE_INGESTION,
};
const ENGINE_LABELS = { snowflake: 'Snowflake', postgres: 'Postgres', r: 'R' };

export default {
  name: 'AgentExplore',
  components: {
    Chart_V5, Chart_V6, Sankey_Diagram, HexMap, Pie_Chart,
    AgentConversation, AgentUploadModal, AgentRunOutput, AgentHtmlCard, D3Insight, PageCanvas,
  },
  props: {
    autoStart: { type: Boolean, default: false },
    sessionId: { type: String, default: null },
    metricId: { type: [Number, String], default: null },
    studyId: { type: [Number, String], default: null },
    s3Key: { type: String, default: '' },
    schemaId: { type: [Number, String], default: null },
  },
  data() {
    const entry = entryForSession(
      this.sessionId,
      `${this.$store.state.tenant.current_tenant_slug || ''}:${this.$store.state.auth.current_user?.id || ''}:${this.metricId || ''}:${this.studyId || ''}:${this.s3Key || ''}`,
    );
    return {
      entry,
      s: entry.state,
      showUpload: false,
      runPollTimers: {},
      uploadIcon: faUploadBtn,
      openKey: '',      // the expanded row; '' means follow the newest
      openPinned: false, // set once the user opens one, so new work stops stealing it
      convWidth: layoutStore.load().conv,
      resizing: false,
      lane: 'all',
    };
  },
  computed: {
    context() {
      return { metricId: this.metricId, studyId: this.studyId, s3Key: this.s3Key, schemaId: this.schemaId };
    },
    storageKey() {
      return `agent-session:${this.$store.state.tenant.current_tenant_slug || ''}:${this.$store.state.auth.current_user?.id || ''}:${this.metricId || ''}:${this.studyId || ''}:${this.s3Key || ''}`;
    },
    sessionActive() {
      return ['connecting', 'launching', 'ready', 'thinking', 'rate_limited'].includes(this.s.connState);
    },
    canUpload() {
      return canAccessFeature(FEATURES.ACTION_AGENT_UPLOAD);
    },
    canSavePage() {
      return canAccessFeature(FEATURES.ACTION_AGENT_SAVE_PAGES);
    },
    persistedArtifacts() {
      return this.s.artifacts.filter((a) => !['visual', 'drawing', 'file', 'document', 'plan', 'report'].includes(a.kind));
    },
    notebookItems() {
      const flow = this.s.artifacts.filter((a) => NOTEBOOK_ARTIFACT_KINDS.has(a.kind));
      const absorbed = new Set(this.s.drafts.map((d) => `preview_${d.id}`.slice(0, 64)));
      const cells = this.s.notebook.filter((c) => !absorbed.has(c.id));
      const drafts = this.s.drafts.map((d) => ({ ...d, kind: 'draft', draftKind: d.kind, _ref: d }));
      return [...cells, ...drafts, ...flow].sort((x, y) => (x.order || 0) - (y.order || 0));
    },
    filesItems() {
      const represented = new Set(this.s.drafts.filter((d) => d.savedId).map((d) => `${DRAFT_LEDGER_KIND[d.kind] || d.kind}:${d.savedId}`));
      return this.s.artifacts.filter((a) => a.kind && a.label && !NOTEBOOK_ARTIFACT_KINDS.has(a.kind) && a.kind !== 'report' && !represented.has(`${a.kind}:${a.id}`));
    },
    reportItems() {
      return this.s.artifacts.filter((a) => a.kind === 'report');
    },
    ledgerGroups() {
      const groups = [];
      const entry = (a) => {
        const base = { key: a.key, group: 'produced', anchor: true, scroll: a.key,
                       focused: this.focused && this.focused.key === a.key };
        if (a.kind === 'draft') {
          return { ...base, iconKind: a.draftKind, label: a.label || 'Draft',
                   name: a.name || a.id, dot: this.draftDot(a) };
        }
        if (a.kind) {
          return { ...base, iconKind: a.kind, label: a.label || a.kind,
                   name: a.name || a.title || a.id, mono: !!a.mono, dot: this.statusDot(a.status) };
        }
        return { ...base, iconKind: a.engine, label: this.engineLabel(a.engine),
                 name: a.label || a.id, mono: !a.label,
                 dot: this.cellStatus(a).tone === 'is-error' ? '#c23a28' : (a.result ? '#2f8f5f' : '#b0aca3') };
      };
      const produced = this.notebookItems.map(entry).reverse();
      // 'produced' is the accordion above; the ledger holds only what is NOT
      // a live session artifact.
      const saved = this.filesItems.map((a) => ({
        key: a.key, group: 'saved', anchor: true, ref: a, iconKind: a.kind, label: a.label,
        name: a.name, mono: !!a.mono, dot: this.statusDot(a.status),
        hint: a.kind === 'frame' ? `Download ${a.name} as CSV` : `${a.label} · ${a.status}`,
        chip: a.kind === 'frame' ? 'CSV · Download' : null,
      }));
      if (saved.length) groups.push({ id: 'saved', label: 'Saved to the platform', items: saved });
      const reports = this.reportItems.map((r) => ({
        key: r.key, group: 'reports', iconKind: 'report', label: 'PDF',
        name: r.title || r.name, dot: '#2f8f5f', chip: `${this.formatBytes(r.size)} · Download`,
        hint: `Download ${r.name}`, report: r,
      }));
      if (reports.length) groups.push({ id: 'reports', label: 'Reports', items: reports });
      const uploads = this.s.files.map((f) => ({
        key: `upload:${f.key}`, group: 'uploads', ref: f, iconKind: 'upload', label: 'Upload',
        name: f.name, dot: '#35617f', hint: f.key,
      }));
      if (uploads.length) groups.push({ id: 'uploads', label: 'Uploads', items: uploads });
      return groups;
    },
    ledgerItems() {
      return this.ledgerGroups.reduce((n, g) => n + g.items.length, 0);
    },
    lanes() {
      const drafts = this.s.drafts.length;
      const out = [{ id: 'all', label: 'All', count: this.notebookItems.length + this.ledgerItems }];
      Object.entries(NOTEBOOK_LANES).forEach(([id, lane]) => {
        const count = this.notebookItems.filter(lane.match).length;
        if (count > 0) out.push({ id, label: lane.label, count });
      });
      if (drafts > 0) out.push({ id: 'drafts', label: 'Drafts', count: drafts });
      this.ledgerGroups.forEach((g) => out.push({ id: g.id, label: g.label === 'Saved to the platform' ? 'Saved' : (g.label === 'In this session' ? 'Work' : g.label), count: g.items.length }));
      return out;
    },
    // The panel shows ONE artifact at a time: a dashboard needs the whole width,
    // not a card in a stack. Unpinned, it follows the newest thing the agent
    // produced; once the user picks from the rail it stays put.
    // The row that is expanded: the newest, until the user opens another.
    openArtifact() {
      const items = this.visibleNotebook;
      if (!items.length) return null;
      if (this.openKey) {
        const picked = items.find((a) => a.key === this.openKey);
        if (picked) return picked;
      }
      return items[items.length - 1];
    },
    visibleNotebook() {
      if (this.lane === 'all') return this.notebookItems;
      if (this.lane === 'drafts') return this.notebookItems.filter((a) => a.kind === 'draft');
      if (NOTEBOOK_LANES[this.lane]) return this.notebookItems.filter(NOTEBOOK_LANES[this.lane].match);
      if (this.lane === 'produced') return this.notebookItems.filter((a) => a.kind !== 'draft');
      return [];
    },
    visibleLedger() {
      if (this.lane === 'all') return this.ledgerGroups;
      return this.ledgerGroups.filter((g) => g.id === this.lane);
    },
    sessionPill() {
      const drafts = this.s.drafts.filter((d) => d.status !== 'saved').length;
      if (drafts && !this.persistedArtifacts.length) return { text: `${drafts} ${drafts === 1 ? 'draft' : 'drafts'} · nothing saved`, bg: '#f0eee8', fg: '#56534d', dot: '#d9a441' };
      const inProd = this.persistedArtifacts.some((a) => (a.runs && a.runs.length) || a.status === 'running in production');
      if (inProd) return { text: 'In production', bg: '#e7f2ea', fg: '#1f6a45', dot: '#2f8f5f' };
      if (this.persistedArtifacts.length) {
        return { text: 'Saved', bg: '#f0eee8', fg: '#56534d', dot: '#2f8f5f' };
      }
      return { text: 'Nothing saved', bg: '#f0eee8', fg: '#56534d', dot: '#b0aca3' };
    },
  },
  mounted() {
    this.entry.view = this;
    this.s.artifacts
      .filter((a) => a.kind === 'run' && ['pending', 'running'].includes(a.status))
      .forEach((a) => this.startRunPolling(a.id));
    this.s.artifacts
      .filter((a) => a.kind === 'template_run' && ['pending', 'running'].includes(a.status))
      .forEach((a) => this.startTemplateRunPolling(a.id));
    this.s.notebook
      .filter((c) => c.result && c.result.kind === 'r' && ['pending', 'running'].includes(c.result.status))
      .forEach((c) => this.startRunPolling(c.result.runId));
  },
  beforeUnmount() {
    if (this.entry.view === this) this.entry.view = null;
    Object.keys(this.runPollTimers).forEach((id) => this.stopRunPolling(id));
  },
  methods: {
    showVersion,
    removeArtifact,
    deleteSavedPage,
    isUnsaved(a) {
      if (!a) return false;
      if (a.kind === 'draft') return a.status !== 'saved';
      if (a.versions && a.versions.length) return !a.versions[a.current] || !a.versions[a.current].saved;
      return false;
    },
    savePage(a) {
      const v = a.versions && a.versions[a.current];
      if (!v || !this.$refs.conversation) return;
      this.$refs.conversation.sendPageSave(a, v);
    },
    isOpen(a) {
      return !!this.openArtifact && this.openArtifact.key === a.key;
    },
    toggleFold(a) {
      if (this.isOpen(a)) { this.openKey = '__none__'; this.openPinned = true; return; }
      this.openKey = a.key;
      this.openPinned = true;
    },
    rowIcon(a) {
      if (a.kind === 'draft') return a.draftKind;
      return a.engine || a.kind || 'file';
    },
    rowLabel(a) {
      if (a.engine) return this.engineLabel(a.engine);
      if (a.kind === 'draft') return a.label || 'Draft';
      return a.label || a.kind || 'Artifact';
    },
    rowName(a) {
      if (a.engine) return a.label || a.id || '';
      return a.name || a.title || a.id || '';
    },
    rowDot(a) {
      if (a.kind === 'draft') return this.draftDot(a);
      if (a.engine) return this.cellStatus(a).tone === 'is-error' ? '#c23a28' : (a.result ? '#2f8f5f' : '#b0aca3');
      return this.statusDot(a.status);
    },
    ledgerClick(it) {
      if (it.group === 'reports') { this.downloadReport(it.report); return; }
      if (it.group === 'saved' && it.ref && it.ref.kind === 'frame') { this.downloadFrame(it.ref); return; }
      if (it.scroll) { this.openKey = it.scroll; this.openPinned = true; return; }
      if (!it.ref) return;
      if (it.ref) it.ref.expanded = !it.ref.expanded;
    },
    startResize(e) {
      if (e.button !== 0) return;
      const conv = this.$refs.conversation && this.$refs.conversation.$el;
      if (!conv) return;
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = conv.getBoundingClientRect().width;
      const max = Math.max(CONV_MIN, this.$refs.grid.getBoundingClientRect().width - SPLITTER_W - WORKSPACE_MIN);
      this.resizing = true;
      const move = (ev) => {
        this.convWidth = Math.round(Math.min(max, Math.max(CONV_MIN, startWidth + ev.clientX - startX)));
      };
      const stop = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', stop);
        window.removeEventListener('pointercancel', stop);
        this.resizing = false;
        layoutStore.save({ conv: this.convWidth });
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', stop);
      window.addEventListener('pointercancel', stop);
    },
    resetSplit() {
      this.convWidth = null;
      layoutStore.save({ conv: null });
    },
    onSession(sessionId) {
      if (!sessionId || sessionId === this.$route.params.sessionId) return;
      this.$router.replace({ name: 'Agent', params: { sessionId }, query: this.$route.query });
    },
    onReset() {
      Object.keys(this.runPollTimers).forEach((id) => this.stopRunPolling(id));
    },
    scrollChat() {
      if (this.$refs.conversation) this.$refs.conversation.scrollChat();
    },
    formatBytes(n) {
      return this.$refs.conversation ? this.$refs.conversation.formatBytes(n) : '';
    },
    downloadReport(report) {
      this.$refs.conversation.downloadFile(report);
    },
    async downloadFrame(frame) {
      if (exportBlockedInDemoMode()) return;
      try {
        const { data } = await axios.get(`/api/impact_analytics/frames/${frame.id}/download/`, { responseType: 'blob' });
        const url = URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${String(frame.name || 'frame').replace(/[^a-z0-9]+/gi, '_')}_${frame.id}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      } catch (err) {
        window.alert(err?.response?.status === 403 ? 'Your role cannot download patient-level frames.' : 'The frame could not be downloaded.');
      }
    },
    onUploaded(file) {
      this.$refs.conversation.attachUpload(file);
    },
    onUploadFailed(file) {
      this.$refs.conversation.uploadFailed(file);
    },
    startRunPolling(runId) {
      if (!runId || this.runPollTimers[runId]) return;
      const poll = async () => {
        try {
          const { data } = await axios.get(`/api/impact_analytics/study-runs/${runId}/`);
          applyRunOutput(this.entry, {
            run_id: data.id,
            status: data.status,
            error: data.error_message || '',
            console: data.console_output || '',
            results: data.results || {},
          });
        } catch {
          this.stopRunPolling(runId);
        }
      };
      this.runPollTimers[runId] = setInterval(poll, 2500);
      poll();
    },
    startTemplateRunPolling(runId) {
      const key = `template:${runId}`;
      if (!runId || this.runPollTimers[key]) return;
      const poll = async () => {
        try {
          const { data } = await axios.get(`/api/hermes/runs/${runId}/`);
          const p = data.progress || {};
          const progress = p.units ? `${(p.done || 0) + (p.failed || 0)} of ${p.units} units${p.failed ? `, ${p.failed} failed` : ''}` : (p.turns ? `${p.turns} turns` : '');
          upsertArtifact(this.entry, {
            key: artifactKey('template_run', runId), status: data.status, progress, error: data.error || '',
          });
          if (!['pending', 'running'].includes(data.status)) this.stopRunPolling(key);
        } catch {
          this.stopRunPolling(key);
        }
      };
      this.runPollTimers[key] = setInterval(poll, 5000);
      poll();
    },
    async cancelTemplateRun(artifact) {
      upsertArtifact(this.entry, { key: artifact.key, cancelling: true });
      try {
        await axios.post(`/api/hermes/runs/${artifact.id}/cancel/`);
      } catch (error) {
        upsertArtifact(this.entry, { key: artifact.key, cancelling: false, error: error.response?.data?.error || 'Cancel failed.' });
      }
    },
    stopRunPolling(runId) {
      if (this.runPollTimers[runId]) clearInterval(this.runPollTimers[runId]);
      delete this.runPollTimers[runId];
    },
    async fetchRunPlots(runId) {
      const holder = runHolder(this.entry, runId);
      if (!holder || holder.status !== 'completed') return;
      const urls = {};
      for (const output of (holder.outputs || []).filter((o) => o.kind === 'plot')) {
        const file = plotKey(output);
        const name = file.replace(/^plots\//, '').replace(/\.png$/, '');
        if (!name || holder.plotUrls[file]) {
          urls[file] = holder.plotUrls[file] || '';
          continue;
        }
        try {
          const response = await axios.get(
            `/api/impact_analytics/study-runs/${runId}/artifact/`,
            { params: { name }, responseType: 'blob' },
          );
          urls[file] = URL.createObjectURL(response.data);
        } catch {
          urls[file] = '';
        }
      }
      if (holder.kind === 'r') holder.plotUrls = urls;
      else upsertArtifact(this.entry, { key: holder.key, plotUrls: urls });
    },
    kindIcon(kind) {
      return KIND_ICONS[kind] || faFileLines;
    },
    kindColor(kind) {
      return KIND_COLORS[kind] || '#56534d';
    },
    engineLabel(engine) {
      return ENGINE_LABELS[engine] || engine;
    },
    editorRows(text) {
      return Math.min(Math.max(String(text || '').split('\n').length + 1, 3), 30);
    },
    formatCount(n) {
      return typeof n === 'number' ? n.toLocaleString() : String(n ?? '');
    },
    cellStale(cell) {
      return !!(cell.result && cell.result.version !== cell.version);
    },
    cellStatus(cell) {
      const r = cell.result;
      if (!r) return { text: 'not run', tone: 'is-muted' };
      if (r.kind === 'r') {
        if (['pending', 'running'].includes(r.status)) return { text: r.status === 'pending' ? 'queued' : 'running…', tone: 'is-busy' };
        if (r.status === 'failed') return { text: 'failed', tone: 'is-error' };
        return { text: `run ${r.runId} · ${(r.outputs || []).length} outputs`, tone: 'is-ok' };
      }
      if (!r.ok) return { text: 'error', tone: 'is-error' };
      const secs = typeof r.elapsedMs === 'number' ? ` · ${(r.elapsedMs / 1000).toFixed(1)}s` : '';
      return { text: `${this.formatCount(r.rowCount)} rows${secs}`, tone: 'is-ok' };
    },
    startEdit(cell) {
      cell.draft = cell.code;
      cell.editing = true;
      cell.error = '';
    },
    cancelEdit(cell) {
      cell.editing = false;
      cell.draft = '';
    },
    async saveEdit(cell) {
      const code = String(cell.draft || '');
      if (!code.trim() || cell.saving) return;
      if (code === cell.code) {
        this.cancelEdit(cell);
        return;
      }
      cell.saving = true;
      cell.error = '';
      try {
        const { data } = await axios.post(turnUrl(this.entry), {
          type: 'block_edit', block_id: cell.id, engine: cell.engine, code,
        });
        if (data.seq) this.entry.ownSeqs.add(data.seq);
        cell.code = code;
        cell.version += 1;
        cell.by = 'user';
        cell.editing = false;
        cell.draft = '';
      } catch (err) {
        cell.error = (err && err.response && err.response.data && err.response.data.error) || 'Could not save the edit.';
      } finally {
        cell.saving = false;
      }
    },
    runCell(cell) {
      if (cell.editing) return;
      this.$refs.conversation.sendAction('run', cell.id);
    },
    actionVerb(verb) {
      return ACTION_VERBS[verb] || 'Running';
    },
    canSaveDraft(item) {
      const feature = DRAFT_SAVE_FEATURE[item.draftKind || item.kind];
      return !feature || canAccessFeature(feature);
    },
    draftAction(verb, item) {
      const draft = item._ref || item;
      if (draft.editing) return;
      this.$refs.conversation.sendAction(verb, draft.id);
    },
    draftDot(d) {
      if (d.error) return '#c23a28';
      if (d.status === 'saved') return '#2f8f5f';
      if (d.status === 'changed') return '#d9a441';
      return '#b0aca3';
    },
    draftChip(d) {
      if (d.status === 'saved') return `Saved #${d.savedId}`;
      if (d.status === 'changed') return `Changed since save #${d.savedId}`;
      return 'Draft';
    },
    codeField(d) {
      const kind = d.draftKind || d.kind;
      if (kind === 'data_attribute') return 'sql_query';
      if (kind === 'study') return 'script';
      return '';
    },
    draftJson(d) {
      const rest = { ...(d.spec || {}) };
      const field = this.codeField(d);
      if (field) delete rest[field];
      return JSON.stringify(rest, null, 2);
    },
    draftCode(d) {
      const kind = d.draftKind || d.kind;
      if (kind === 'data_attribute') return d.spec.sql_query || '';
      if (kind === 'study') return d.spec.script || '';
      return '';
    },
    toggleDraft(item) {
      const d = item._ref || item;
      d.collapsed = !d.collapsed;
    },
    startDraftEdit(item) {
      const d = item._ref || item;
      d.collapsed = false;
      d.edit = { json: this.draftJson(d), code: this.codeField(d) ? (d.spec[this.codeField(d)] || '') : '' };
      d.editing = true;
      d.editError = '';
    },
    cancelDraftEdit(item) {
      const d = item._ref || item;
      d.editing = false;
      d.edit = {};
    },
    async saveDraftEdit(item) {
      const d = item._ref || item;
      if (d.saving) return;
      let spec;
      try {
        spec = JSON.parse(d.edit.json || '');
      } catch (err) {
        d.editError = `Not valid JSON: ${err.message}`;
        return;
      }
      if (!spec || typeof spec !== 'object' || Array.isArray(spec)) {
        d.editError = 'The spec must be a JSON object.';
        return;
      }
      const field = this.codeField(d);
      if (field) {
        if (!String(d.edit.code || '').trim()) { d.editError = `${field === 'script' ? 'The script' : 'The SQL'} cannot be empty.`; return; }
        spec[field] = d.edit.code;
      }
      if (JSON.stringify(spec) === JSON.stringify(d.spec)) { this.cancelDraftEdit(d); return; }
      d.saving = true;
      d.editError = '';
      try {
        const { data } = await axios.post(turnUrl(this.entry), { type: 'draft_edit', draft_id: d.id, spec });
        if (data.seq) this.entry.ownSeqs.add(data.seq);
        applyDraftEdit(this.entry, d.id, spec);
        d.editing = false;
        d.edit = {};
      } catch (err) {
        d.editError = (err && err.response && err.response.data && err.response.data.error) || 'Could not save the edit.';
      } finally {
        d.saving = false;
      }
    },
    factsFor(a) {
      const facts = [];
      Object.entries(a.detail || {}).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined) return;
        facts.push({ k: String(k).replace(/_/g, ' '), v: String(v) });
      });
      return facts;
    },
    statusDot(status) {
      const st = String(status || '').toLowerCase();
      if (['running in production', 'completed', 'live', 'success', 'succeeded', 'validated', 'verified'].includes(st)) return '#2f8f5f';
      if (['failed', 'rejected', 'deleted', 'error'].includes(st)) return '#c23a28';
      if (['running', 'queued', 'pending', 'in_progress', 'building'].includes(st)) return '#d9a441';
      return '#b0aca3';
    },
    scrollArtifacts() {
      this.$nextTick(() => {
        const el = this.$refs.artifactScroll;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    artifactDomId(key) {
      return `artifact-${key.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
    },
    scrollToArtifact(key) {
      const el = document.getElementById(this.artifactDomId(key));
      const sc = this.$refs.artifactScroll;
      if (el && sc) {
        const top = el.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop - 16;
        sc.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      } else if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      const target = this.s.drafts.find((d) => d.key === key)
        || this.s.notebook.find((c) => c.key === key)
        || this.s.artifacts.find((a) => a.key === key);
      if (target) {
        if (target.kind === 'draft' || this.s.drafts.includes(target)) target.collapsed = false;
        target.flash = true;
        setTimeout(() => { target.flash = false; }, 1200);
      }
    },
    draftPreview(a) {
      const id = `preview_${a.id}`.slice(0, 64);
      return this.s.notebook.find((c) => c.id === id) || null;
    },
    previewStale(a) {
      const d = a._ref || a;
      return typeof d.previewedVersion === 'number' && d.version > d.previewedVersion;
    },
    previewDiffers(a) {
      const p = this.draftPreview(a);
      if (!p) return false;
      const inner = String(p.code || '').replace(/^SELECT \* FROM \(\s*/i, '').replace(/\s*\) _preview LIMIT \d+\s*$/i, '').trim();
      return inner !== String(this.draftCode(a) || '').trim();
    },
    drawingDoc,
    drawingAspect(a) {
      const m = /viewBox\s*=\s*["']\s*[\d.-]+\s+[\d.-]+\s+([\d.]+)\s+([\d.]+)/.exec(a.svg || '');
      if (m && Number(m[2]) > 0) return `${m[1]} / ${m[2]}`;
      return '2 / 1';
    },
    formatWhen(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>
